/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { RouterProps, showNotification } from '../App';
import { client } from '..';
// tslint:disable-next-line: max-line-length
import { ApplicationSettingsData, ChargeRateSyncSettings, ClientNotesSettings, DataPublisherSettings, DocumentPublisherSettings, FutureTimeSynchronization, LabelSettings, Licencing, MobileSettings, RatesSyncSettings, retrieveApplicationSettingsData, SettingName, StaffBudgetSettings, SystemOptions, TaskPublisherSettings } from './ApplicationSettingsRepository';
import { SettingNotificationSubscription } from '../firmSettingConfiguration/FirmSettingConfigurationRepository';
import DocumentPublishingTab from './tabs/DocumentPublishingTab';
import DataPublishingTab from './tabs/DataPublishingTab';
import LicencingTab from './tabs/LicencingTab';
import FutureTimeSynchronizationTab from './tabs/FutureTimeSynchronizationTab';
import TaskPublishingTab from './tabs/TaskPublishingTab';
import ClientNotesTab from './tabs/ClientNotesTab';
import LabelSettingsTab from './tabs/LabelSettingsTab';
import SystemOptionsTab from './tabs/SystemOptionsTab';
import PublishingAndSynchronisationTab from './tabs/PublishingAndSynchronisationTab';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import UpdateOutlinedIcon from '@material-ui/icons/UpdateOutlined';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import SpeakerNotesOutlinedIcon from '@material-ui/icons/SpeakerNotesOutlined';
import SyncOutlinedIcon from '@material-ui/icons/SyncOutlined';
import { FilteredVerticalTabPage, SidebarItem, SidebarGroup } from '../v2/layouts/FilteredVerticalTabPage';
import { SkeletonLoader } from '../components/loading/SkeletonLoader';
import DataFieldMappingsSettingsTab from './tabs/DataFieldMappingsSettingsTab';
import FieldLabelsTab from './tabs/FieldLabelsTab';

export enum ApplicationSettingsList {
    DataPublishing = 'DataPublishing',
    DocumentPublishing = 'DocumentPublishing',
    Licencing = 'Licencing',
    // RVMobile = 'RVMobile',
    FutureTimeSynchronization = 'FutureTimeSynchronization',
    TaskPublishing = 'TaskPublishing',
    ClientNotes = 'ClientNotes',
    LabelSettings = 'LabelSettings',
    SettingOptions = 'SystemOptions',
    PublishingAndSynchronisation = 'PublishingAndSynchronisation',
    DataFieldMappings = 'DataFieldMappings',
    FieldLabels = 'FieldLabels',
}

interface ApplicationSettingsPageState {
    tabs: SettingName[];
    isTabLoading: boolean;
    mobileSettings:            MobileSettings | null;
    dataPublisherSettings:     DataPublisherSettings | null;
    documentPublisherSettings: DocumentPublisherSettings | null;
    taskPublisherSettings: TaskPublisherSettings | null;
    licencing: Licencing | null;
    staffBudgetSettings: StaffBudgetSettings | null;
    futureTimeSynchronization: FutureTimeSynchronization | null;
    clientNotesSettings: ClientNotesSettings | null;
    labelSettings: LabelSettings | null;
    systemOptions: SystemOptions | null;
    ratesSyncSettings: RatesSyncSettings | null;
    chargeRateSyncSettings: ChargeRateSyncSettings | null;
}

interface ApplicationSettingsPageProps extends RouterProps {

}

const SETTINGS_GROUPS: SidebarGroup[] = [
    {
        key: 'settings',
        label: 'Application Settings',
        icon: <SettingsOutlinedIcon />,
        iconColor: '#1565c0',
    },
];

// Tabs excluded from the sidebar — edited only via the combined PublishingAndSynchronisation tab.
const EXCLUDED_TABS = [
    'RatesSyncSettings',
    'StaffBudgets',
    'ChargeRateSyncSettings',
    'ClientNotes',
    'DocumentPublishing',
    'FutureTimeSynchronization',
    'TaskPublishing',
];

// tslint:disable-next-line: no-any
const SETTINGS_META: Record<string, { icon: React.ReactNode; iconBg: string; iconColor: string }> = {
    ClientNotes: {
        icon: <SpeakerNotesOutlinedIcon fontSize="small" />,
        iconBg: '#e8f5e9',
        iconColor: '#2e7d32',
    },
    DataPublishing: {
        icon: <CloudUploadOutlinedIcon fontSize="small" />,
        iconBg: '#e3f2fd',
        iconColor: '#1565c0',
    },
    DocumentPublishing: {
        icon: <InsertDriveFileOutlinedIcon fontSize="small" />,
        iconBg: '#fff3e0',
        iconColor: '#e65100',
    },
    TaskPublishing: {
        icon: <AssignmentOutlinedIcon fontSize="small" />,
        iconBg: '#f3e5f5',
        iconColor: '#7b1fa2',
    },
    Licencing: {
        icon: <VpnKeyOutlinedIcon fontSize="small" />,
        iconBg: '#fce4ec',
        iconColor: '#ad1457',
    },
    FutureTimeSynchronization: {
        icon: <UpdateOutlinedIcon fontSize="small" />,
        iconBg: '#e0f2f1',
        iconColor: '#00695c',
    },
    LabelSettings: {
        icon: <TextFieldsIcon fontSize="small" />,
        iconBg: '#ede7f6',
        iconColor: '#4527a0',
    },
    SystemOptions: {
        icon: <SettingsOutlinedIcon fontSize="small" />,
        iconBg: '#eceff1',
        iconColor: '#455a64',
    },
    PublishingAndSynchronisation: {
        icon: <SyncOutlinedIcon fontSize="small" />,
        iconBg: '#e1f5fe',
        iconColor: '#0277bd',
    },
    FieldLabels: {
        icon: <TextFieldsIcon fontSize="small" />,
        iconBg: '#ede7f6',
        iconColor: '#4527a0',
    }
};

export default function ApplicationSettingsPage(props: ApplicationSettingsPageProps) {

    const [pageState, setPageState] = useState<ApplicationSettingsPageState>({
        tabs: [],
        isTabLoading: true,
        mobileSettings:            null,
        dataPublisherSettings:     null,
        documentPublisherSettings: null,
        taskPublisherSettings:     null,
        licencing:                 null,
        staffBudgetSettings:       null,
        futureTimeSynchronization: null,
        clientNotesSettings:       null,
        labelSettings:             null,
        systemOptions:             null,
        ratesSyncSettings:         null,
        chargeRateSyncSettings:    null,
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchTabData();
        // tslint:disable-next-line: align
    }, []);

    useEffect(() => {
        const subscribeSettingNotificationObj = subscribeSettingNotification();
        // Cleanup
        return () => {
            subscribeSettingNotificationObj.unsubscribe();
        };

    // tslint:disable-next-line: align
    }, []);

    const subscribeSettingNotification = () => {
        return (
            client
                .subscribe({
                    fetchPolicy: 'no-cache',
                    query: SettingNotificationSubscription,
                    variables: {},
                })
                .subscribe({
                    // tslint:disable-next-line:no-any
                    next(results: any) {
                        if (results.data.settingNotification.status) {
                            showNotification('Success', results.data.settingNotification.message, 'info');

                        } else {
                            showNotification('Failed', results.data.settingNotification.message, 'error');
                        }
                    },
                    // tslint:disable-next-line:no-any
                    error(err: any) {
                        // tslint:disable-next-line:no-console
                        console.error(err);
                        showNotification(null, err, 'error');
                    },
                })
        );
    };

    const fetchTabData = () => {
        retrieveApplicationSettingsData(
            true,
            // tslint:disable-next-line: no-console
            (data: ApplicationSettingsData) => onApplicationSettingsRetrieved(data),
            // tslint:disable-next-line: no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');
                setPageState(prev => ({ ...prev, isTabLoading: false }));
            }
        );
    };

    const onApplicationSettingsRetrieved = (data: ApplicationSettingsData) => {
        setPageState((prevState) => {
            return {
                ...prevState,
                tabs: data.settings.applications.settingNames.settingName,
                mobileSettings:    data.settings.applications.mobileSettings,
                dataPublisherSettings:     data.settings.applications.dataPublisherSettings,
                documentPublisherSettings: data.settings.applications.documentPublisherSettings,
                taskPublisherSettings: data.settings.applications.taskPublisherSettings,
                licencing: data.settings.applications.licencing,
                staffBudgetSettings: data.settings.applications.staffBudgetSettings,
                futureTimeSynchronization: data.settings.applications.futureTimeSynchronization,
                clientNotesSettings: data.settings.applications.clientNotesSettings,
                labelSettings: data.settings.applications.labelSettings,
                systemOptions: data.settings.applications.systemOptions,
                ratesSyncSettings: data.settings.applications.ratesSyncSettings,
                chargeRateSyncSettings: data.settings.applications.chargeRateSyncSettings,
                isTabLoading: false
            };
        });
    };

    const sidebarItems: SidebarItem[] = useMemo(
        () => pageState.tabs
            .filter(tab => !EXCLUDED_TABS.includes(tab.internalName))
            .map(tab => {
                const meta = SETTINGS_META[tab.internalName] ?? {
                    icon: <SettingsOutlinedIcon fontSize="small" />,
                    iconBg: '#eceff1',
                    iconColor: '#455a64',
                };
                return {
                    id: tab.internalName,
                    name: tab.title,
                    description: tab.description,
                    group: 'settings',
                    ...meta,
                };
            }),
        [pageState.tabs]
    );

    const defaultSelectedId = props.match.params.tab || ApplicationSettingsList.LabelSettings;

    const [selectedId, setSelectedId] = useState<string>(defaultSelectedId);

    const selectedSidebarItem = useMemo(
        () => sidebarItems.find(item => item.id === selectedId) ?? null,
        [sidebarItems, selectedId]
    );

    const onItemSelect = (item: SidebarItem) => {
        props.history.push(`/applicationSettings/${item.id}`);
        setSelectedId(item.id);
    };

    const onClickBack = () => {
        props.history.push('/configurationDashboard');
    };

    return (
        <FilteredVerticalTabPage
            icon={<SettingsOutlinedIcon fontSize="small" />}
            title="Application Settings"
            subtitle="Configure firm-wide application settings and preferences"
            onBack={onClickBack}
            backTooltip="Back to Configuration"
            enableSearch={true}
            searchPlaceholder="Search settings…"
            sidebarGroups={SETTINGS_GROUPS}
            sidebarItems={sidebarItems}
            defaultSelectedId={defaultSelectedId}
            sidebarLoading={pageState.isTabLoading}
            onItemSelect={onItemSelect}
            contentControl={{
                title: selectedSidebarItem?.name,
                subtitle: selectedSidebarItem?.description,
                icon: selectedSidebarItem?.icon,
                iconBg: selectedSidebarItem?.iconBg,
                iconColor: selectedSidebarItem?.iconColor,
                loading: pageState.isTabLoading,
            }}
            renderContent={(selectedItem) => {
                if (pageState.isTabLoading) {
                    return <SkeletonLoader variant="form" rows={4} columns={2} />;
                }
                if (!selectedItem) {
                    return null;
                }
                if (selectedItem.id === ApplicationSettingsList.ClientNotes) {
                    return (
                        <ClientNotesTab
                            data={pageState.clientNotesSettings}
                            onSave={(data: ClientNotesSettings | null) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        clientNotesSettings: data
                                    };
                                });
                            }}
                        />
                    );
                }
                if (selectedItem.id === ApplicationSettingsList.DataPublishing) {
                    return (
                        <DataPublishingTab
                            data={pageState.dataPublisherSettings}
                            onSave={(data: DataPublisherSettings | null) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        dataPublisherSettings: data
                                    };
                                });
                            }}
                        />
                    );
                }
                if (selectedItem.id === ApplicationSettingsList.DocumentPublishing) {
                    return (
                        <DocumentPublishingTab
                            data={pageState.documentPublisherSettings}
                            onSave={(data: DocumentPublisherSettings | null) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        documentPublisherSettings: data
                                    };
                                });
                            }}
                        />
                    );
                }
                if (selectedItem.id === ApplicationSettingsList.TaskPublishing) {
                    return (
                        <TaskPublishingTab
                            data={pageState.taskPublisherSettings}
                            onSave={(data: TaskPublisherSettings | null) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        taskPublisherSettings: data
                                    };
                                });
                            }}
                        />
                    );
                }
                if (selectedItem.id === ApplicationSettingsList.Licencing) {
                    return (
                        <LicencingTab
                            data={pageState.licencing}
                            onSave={(data: Licencing | null) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        licencing: data
                                    };
                                });
                            }}
                        />
                    );
                }
                if (selectedItem.id === ApplicationSettingsList.FutureTimeSynchronization) {
                    return (
                        <FutureTimeSynchronizationTab
                            data={pageState.futureTimeSynchronization}
                            onSave={(data: FutureTimeSynchronization | null) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        futureTimeSynchronization: data
                                    };
                                });
                            }}
                        />
                    );
                }
                if (selectedItem.id === ApplicationSettingsList.LabelSettings) {
                    return (
                        <LabelSettingsTab
                            data={pageState.labelSettings}
                            onSave={(data: LabelSettings | null) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        labelSettings: data
                                    };
                                });
                            }}
                        />
                    );
                }
                if (selectedItem.id === ApplicationSettingsList.SettingOptions) {
                    return (
                        <SystemOptionsTab
                            data={pageState.systemOptions}
                            onSave={(data: SystemOptions | null) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        systemOptions: data
                                    };
                                });
                            }}
                        />
                    );
                }
                if (selectedItem.id === ApplicationSettingsList.DataFieldMappings) {
                    return (
                        <DataFieldMappingsSettingsTab/>
                    );
                }
                
                if (selectedItem.id === ApplicationSettingsList.PublishingAndSynchronisation) {
                    return (
                        <PublishingAndSynchronisationTab
                            clinetNotesData={pageState.clientNotesSettings}
                            documentPublisherData={pageState.documentPublisherSettings}
                            futureTimeSynchronizationData={pageState.futureTimeSynchronization}
                            staffBudgetSettingsData={pageState.staffBudgetSettings}
                            taskPublisherSettingsData={pageState.taskPublisherSettings}
                            ratesSyncSettingsData={pageState.ratesSyncSettings}
                            chargeRateSyncSettingsData={pageState.chargeRateSyncSettings}
                            onSave={(clinetNotesData, documentPublisherData, futureTimeSynchronizationData, staffBudgetSettingsData, taskPublisherSettingsData, ratesSyncSettingsData) => {
                                setPageState((prevState) => {
                                    return {
                                        ...prevState,
                                        clientNotesSettings: clinetNotesData,
                                        documentPublisherSettings: documentPublisherData,
                                        futureTimeSynchronization: futureTimeSynchronizationData,
                                        staffBudgetSettings: staffBudgetSettingsData,
                                        taskPublisherSettings: taskPublisherSettingsData,
                                        ratesSyncSettings: ratesSyncSettingsData,
                                        chargeRateSyncSettings: pageState.chargeRateSyncSettings
                                    };
                                });
                            }}
                        />
                    );
                }

                if (selectedItem.id === ApplicationSettingsList.FieldLabels) {
                    return (
                        <FieldLabelsTab />
                    );
                }

                return null;
            }}
        />
    );
}
